/**
 * SyncNudgeButton — DEC-328 "Ask driver to sync" (docs/CLAUDE.md §44, quick 261013-snd).
 *
 * POST /v1/jobs/:id/sync-nudge with `{}` (a body-less JSON POST 500s in Fastify's parser,
 * DEC-201/287). The API records the nudge, pushes the driver, and resolves it when the
 * driver's phone next checks in. The line under the button reads the stuck list's
 * lastNudgeAt / lastNudgeCheckinAt, so it refreshes with the list.
 *
 * A second nudge for the same job inside 10 minutes is refused (409 SYNC_NUDGE_TOO_SOON);
 * the toast shows the API's own message for that and for SYNC_NUDGE_NOT_APPLICABLE.
 *
 * #54: `iconOnly` renders just a small icon button (tooltip + aria-label "Ask driver to sync")
 * for the Stuck jobs row, which prints the status line itself beside its other notes.
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { RefreshCw } from 'lucide-react';
import { ApiError, useApiMutation, useApiOpts } from '@lodex-pro/web-shared';
import { Button } from 'src/components/ui/button';
import { syncNudgeStatusText } from './stuck-jobs-format';

export interface SyncNudgeButtonProps {
  readonly jobId: string;
  readonly lastNudgeAt: string | null;
  readonly lastNudgeCheckinAt: string | null;
  readonly nowMs: number;
  /** #54 — the icon button alone, no status line (the Stuck jobs row prints it). */
  readonly iconOnly?: boolean;
}

const LABEL = 'Ask driver to sync';

/**
 * Plain wording per API error code. Local rather than read from the shared ERROR_CATALOG:
 * the shared barrel pulls drizzle into a web bundle.
 */
export const NUDGE_ERROR_TEXT: Readonly<Record<string, string>> = {
  SYNC_NUDGE_TOO_SOON: 'The driver was asked a few minutes ago. Give them a moment.',
  SYNC_NUDGE_NOT_APPLICABLE:
    'This job is not in progress with a driver, so there is no one to ask.',
};

const statusStyle: React.CSSProperties = {
  fontSize: '12px',
  color: 'var(--color-text-tertiary)',
};

export function SyncNudgeButton({
  jobId,
  lastNudgeAt,
  lastNudgeCheckinAt,
  nowMs,
  iconOnly = false,
}: SyncNudgeButtonProps): React.JSX.Element {
  const apiOpts = useApiOpts();
  const queryClient = useQueryClient();

  const mutation = useApiMutation<Record<string, never>, { id: string }>(
    apiOpts,
    `/v1/jobs/${jobId}/sync-nudge`,
    'POST',
    {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ['jobs'] });
        toast.success('Driver asked to sync');
      },
      onError: (err: unknown) => {
        const code = err instanceof ApiError ? err.code : null;
        toast.error(
          (code !== null ? NUDGE_ERROR_TEXT[code] : undefined) ??
            'Could not ask the driver to sync. Try again.',
        );
      },
    },
  );

  if (iconOnly) {
    return (
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="h-8 w-8"
        title={LABEL}
        aria-label={LABEL}
        disabled={mutation.isPending}
        onClick={(): void => mutation.mutate({})}
      >
        <RefreshCw aria-hidden="true" />
      </Button>
    );
  }

  const status = syncNudgeStatusText(lastNudgeAt, lastNudgeCheckinAt, nowMs);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2px' }}>
      <Button
        type="button"
        variant="outline"
        disabled={mutation.isPending}
        onClick={(): void => mutation.mutate({})}
      >
        {LABEL}
      </Button>
      {status !== null ? (
        <span style={statusStyle} data-testid={`sync-nudge-status-${jobId}`}>
          {status}
        </span>
      ) : null}
    </div>
  );
}
